
import type { StallSlot, StallWindow } from "../net/messages";
import type { SfxPlayer } from "../audio/sfx";
import { NO, YES, type BagModel } from "./bag";
import { formatYang, money } from "../money";
import { itemArtNode } from "./itemart";
import {
  ItemTooltip,
  attachHoverTooltip,
  slotTooltipData,
  wireTooltipData,
  type ItemTooltipData,
} from "./tooltip";
import { elem, installChrome, styleInstaller } from "./chrome";
import { iconCellsTall } from "./itemicon";
import {
  SHOP_COLUMNS,
  SHOP_HEIGHT,
  SHOP_ROWS,
  SHOP_SLOT_COUNT,
  SHOP_WIDTH,
  SHOP_X_FROM_RIGHT,
  SHOP_Y,
  installShopStyle,
} from "./shop";
import stallForbidden from "./stallforbidden.json";

/** `ITEM_ANTIFLAG_GIVE | ITEM_ANTIFLAG_MYSHOP` vnums
 *  (`tools/build_stall_forbidden.py`, `item_length.h:303,306`). */
const STALL_FORBIDDEN: ReadonlySet<number> = new Set(stallForbidden.vnums);

/** One line the builder will send. The wire's three fields and no others --
 *  see the file docstring on why there is no vnum here. */
export interface StallDraftLine {
  readonly sourceSlot: number;
  readonly displayPos: number;
  readonly price: number;
}

/** `DO_YOU_BUY_ITEM` (`locale_game.txt:70-71`, `uishop.py:421`). */
export function buyQuestion(name: string, count: number, price: number): string {
  return count > 1
    ? `Do you want to buy ${name} ${count} for ${formatYang(price)}?`
    : `Do you want to buy ${name} for ${formatYang(price)}?`;
}

export interface StallBuilderActions {
  /** Put the stall up with these listings under this sign. */
  onOpen?: (sign: string, listings: readonly StallDraftLine[]) => void;
  /** drop.wav on a listing or a take-back, loginfail.wav on a refused drop
   *  (`uiprivateshopbuilder.py:260,269,307`). */
  sfx?: SfxPlayer;
  /** A line for the chat: `PRIVATE_SHOP_CANNOT_SELL_ITEM`, the one line
   *  `OnSelectEmptySlot` appends before it would otherwise refuse a drop
   *  silently (`uiprivateshopbuilder.py:236`). */
  onInfo?: (text: string) => void;
}

export interface StallActions {
  /** Buy the shelf in this display cell. */
  onBuy?: (slot: number) => void;
  /** The player shut the window; tell the server so its record goes too. */
  onClose?: () => void;
  /** The player packed up their own stall. Only ever offered on your own. */
  onPackUp?: () => void;
  /** A line for the chat: SHOP_BUY_INFO or SHOP_SELL_INFO (`uishop.py:259,264`). */
  onInfo?: (text: string) => void;
  /** The cursor the window now asks for: `app.BUY`, `app.SELL`, or back to
   *  normal (`uishop.py:258-271`). */
  onShopping?: (mode: StallShopping) => void;
}

/** Which of ShopDialog's two toggles is down, if either. */
export type StallShopping = "buy" | "sell" | null;

/** SHOP_BUY_INFO and SHOP_SELL_INFO (`locale_game.txt:430,438`), double space kept. */
export const SHOP_BUY_INFO = "Choose the Item you want to buy in the shop window.";
export const SHOP_SELL_INFO = "Choose the Item you want to sell  in your inventory window.";
/** PRIVATE_SHOP_CLOSE_QUESTION (`locale_game.txt:362`). */
export const PRIVATE_SHOP_CLOSE_QUESTION = "Do you want to close the private shop?";
/** PRIVATE_SHOP_CANNOT_SELL_ITEM (`locale_en/locale/en/locale_game.txt:361`). */
export const PRIVATE_SHOP_CANNOT_SELL_ITEM = "You cannot sell this item in a private shop.";

/** `SHOP_SIGN_MAX_LEN` (`common/length.h:14`). The server truncates rather than
 *  refusing (`strlcpy`, `char.cpp:592-593`), so this is a courtesy that stops a
 *  seller from typing a name they will not get -- never a validation. */
export const SIGN_MAX_CHARS = 32;

/** PRIVATE_SHOP_INPUT_PRICE_DIALOG_TITLE (`locale_game.txt:364`). */
export const PRIVATE_SHOP_INPUT_PRICE_DIALOG_TITLE = "Price";
/** MONEY_INPUT_DIALOG_SELLPRICE (`locale_game.txt:307`), its trailing tab drawn as a space. */
export const MONEY_INPUT_DIALOG_SELLPRICE = "Sales Price: ";

/** The top cell of the builder's grid an item `rows` tall lands on when it is
 *  released over `cell`: GetPickedSlotList starts a three-tall item one row
 *  above the pointer and pushes any item back inside the grid
 *  (`PythonGridSlotWindow.cpp:163-203`). */
export function pickedCell(cell: number, rows: number): number {
  const height = Math.max(1, Math.min(SHOP_ROWS, rows));
  const top = Math.floor(cell / SHOP_COLUMNS) - (height === 3 ? 1 : 0); // PythonGridSlotWindow.cpp:175-179
  const clamped = Math.max(0, Math.min(top, SHOP_ROWS - height)); // PythonGridSlotWindow.cpp:187-203
  return clamped * SHOP_COLUMNS + (cell % SHOP_COLUMNS);
}

export function listable(slot: { readonly vnum: number } | undefined): boolean {
  return slot !== undefined && slot.vnum > 0;
}

/** What a stall's title bar says. The sign if there is one, the seller's name
 *  if the sign is somehow empty -- which the server refuses to let happen, so
 *  this is the belt to that braces. */
export function stallTitle(sign: string, sellerName: string): string {
  const trimmed = sign.trim();
  return trimmed.length > 0 ? trimmed : `${sellerName}'s stall`;
}

export function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value < 1) return null;
  return value;
}

const STYLE_ID = "pcshop-style";

const LARGE_BUTTON_WIDTH = 88; // large_button_01.sub, CloseButton's face (shopdialog.py:110)
const MIDDLE_BUTTON_WIDTH = 61; // questiondialog.py:45
const ASK_WIDTH = 340; // questiondialog.py:10
const ASK_HEIGHT = 105; // questiondialog.py:11
const BUILDER_HEIGHT = 328 + 26; // privateshopbuilder.py:12
const PRICE_WIDTH = 200; // moneyinputdialog.py:11
const PRICE_HEIGHT = 110; // moneyinputdialog.py:12
const PRICE_SLOT_WIDTH = 90; // moneyinputdialog.py:38
const PRICE_MAX_DIGITS = 9; // uicommon.py:340, :382

const STYLE = `
/* ShopDialog, the one window uishop.py opens for a stall as for an NPC
   counter (uishop.py:153-163): 184x328 at SCREEN_WIDTH - 400, 10
   (shopdialog.py:6-12). */
.stall {
  position: fixed;
  left: max(8px, calc(100vw - ${SHOP_X_FROM_RIGHT}px));
  top: ${SHOP_Y}px;
  z-index: 41;
  width: ${SHOP_WIDTH}px;
  height: ${SHOP_HEIGHT}px;
  pointer-events: auto;
  opacity: 0.999;
}
/* TitleBar at (8, 8), 169 wide (shopdialog.py:35-38, privateshopbuilder.py:35-38). */
.stall > .m2-titlebar,
.stall-builder > .m2-titlebar {
  left: 8px;
  top: 8px;
  width: 169px;
  overflow: hidden;
  white-space: nowrap;
}
/* CloseButton, a large button centred at y = 295 (shopdialog.py:103-113),
   184 / 2 - 88 / 2 across (PythonWindow.cpp:286). */
.stall-packup {
  position: absolute;
  left: ${Math.floor(SHOP_WIDTH / 2) - Math.floor(LARGE_BUTTON_WIDTH / 2)}px;
  top: 295px;
}
/* QuestionDialog: a 340x105 board centred on screen (questiondialog.py:10-11,
   uicommon.py:235-236). */
.stall-ask {
  position: fixed;
  left: 50%;
  top: 50%;
  width: ${ASK_WIDTH}px;
  height: ${ASK_HEIGHT}px;
  margin: -${Math.floor(ASK_HEIGHT / 2)}px 0 0 -${ASK_WIDTH / 2}px;
  z-index: 64;
}
.stall-ask[hidden] { display: none; }
/* message, centred on y = 38 (questiondialog.py:28-37). */
.stall-ask-text {
  position: absolute;
  left: 0;
  right: 0;
  top: 38px;
  transform: translateY(-50%);
  text-align: center;
}
/* accept and cancel, centred at x = -40 and 40, y = 63 (questiondialog.py:39-66,
   PythonWindow.cpp:286). */
.stall-ask-yes, .stall-ask-no { position: absolute; top: 63px; }
.stall-ask-yes { left: ${ASK_WIDTH / 2 - Math.floor(MIDDLE_BUTTON_WIDTH / 2) - 40}px; }
.stall-ask-no { left: ${ASK_WIDTH / 2 - Math.floor(MIDDLE_BUTTON_WIDTH / 2) + 40}px; }
/* PrivateShopBuilder: 184x354, centred on open (privateshopbuilder.py:11-12,
   uiprivateshopbuilder.py:180). */
.stall-builder {
  position: fixed;
  left: 50%;
  top: 50%;
  width: ${SHOP_WIDTH}px;
  height: ${BUILDER_HEIGHT}px;
  margin: -${BUILDER_HEIGHT / 2}px 0 0 -${SHOP_WIDTH / 2}px;
  z-index: 41;
  pointer-events: auto;
  opacity: 0.999;
  font: 11px/1.2 Tahoma, "MS Sans Serif", sans-serif;
}
/* NameSlot, a 157x18 slot bar at (13, 35) (privateshopbuilder.py:47-51). */
.stall-sign-input {
  position: absolute;
  left: 13px;
  top: 35px;
  width: 157px;
  height: 18px;
  box-sizing: border-box;
  padding: 0 3px;
  border: 1px solid rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.5);
  color: inherit;
  font: inherit;
}
/* ItemSlot: 5 x 32 by 8 x 32 at (12, 34 + 26) (privateshopbuilder.py:77-87). */
.stall-builder > .stall-builder-grid {
  left: 12px;
  top: 60px;
}
/* OnRenderPickingSlot's 50%-alpha bar over the cells an attached item would
   land on: white (1,1,1,0.5) while free, red (1,0,0,0.5) once CheckMoving
   finds one occupied (PythonGridSlotWindow.cpp:20-46). */
.stall-builder-grid .shop-slot-hover-ok::after,
.stall-builder-grid .shop-slot-hover-bad::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.stall-builder-grid .shop-slot-hover-ok::after { background: rgba(255, 255, 255, 0.5); }
.stall-builder-grid .shop-slot-hover-bad::after { background: rgba(255, 0, 0, 0.5); }
/* OkButton at (21, 321), CloseButton at (104, 321) (privateshopbuilder.py:88-89, :103-104). */
.stall-open { position: absolute; left: 21px; top: 321px; }
.stall-builder-close { position: absolute; left: 104px; top: 321px; }
.stall-open[disabled] { opacity: 0.4; cursor: default; }
/* MoneyInputDialog: a 200x110 board with a title bar, centred on Open
   (moneyinputdialog.py:11-28, uicommon.py:363). */
.stall-price-dialog {
  position: fixed;
  left: 50%;
  top: 50%;
  width: ${PRICE_WIDTH}px;
  height: ${PRICE_HEIGHT}px;
  margin: -${PRICE_HEIGHT / 2}px 0 0 -${PRICE_WIDTH / 2}px;
  z-index: 64;
  pointer-events: auto;
  opacity: 0.999;
  font: 12px/14px Tahoma, "MS Sans Serif", sans-serif;
}
.stall-price-dialog[hidden] { display: none; }
/* InputSlot, a 90x18 slot bar centred at y = 34 (moneyinputdialog.py:32-41). */
.stall-price-input {
  position: absolute;
  left: ${PRICE_WIDTH / 2 - PRICE_SLOT_WIDTH / 2}px;
  top: 34px;
  width: ${PRICE_SLOT_WIDTH}px;
  height: 18px;
  box-sizing: border-box;
  padding: 0 3px;
  border: 1px solid rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.5);
  color: inherit;
  font: inherit;
}
/* MoneyValue, text centred at y = 59 (moneyinputdialog.py:60-69). */
.stall-price-money { position: absolute; left: 0; right: 0; top: 59px; text-align: center; white-space: nowrap; }
/* AcceptButton and CancelButton, x = -61 - 5 + 30 and 5 + 30 from the centre,
   y = 78 (moneyinputdialog.py:72-98): 200 / 2 - 61 / 2 + x (PythonWindow.cpp:286). */
.stall-price-ok { position: absolute; top: 78px; left: ${PRICE_WIDTH / 2 - Math.floor(MIDDLE_BUTTON_WIDTH / 2) + (-61 - 5 + 30)}px; }
.stall-price-cancel { position: absolute; top: 78px; left: ${PRICE_WIDTH / 2 - Math.floor(MIDDLE_BUTTON_WIDTH / 2) + (5 + 30)}px; }
.stall-empty { color: #9b9484; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, (doc) => {
  installChrome(doc);
  installShopStyle(doc);
});

function shelfTooltipData(shelf: StallSlot): ItemTooltipData {
  return wireTooltipData(shelf, { price: shelf.price });
}

export class StallPanel {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly emptyEl: HTMLElement;
  private readonly packUp: HTMLButtonElement;
  private readonly buyBtn: HTMLButtonElement;
  private readonly sellBtn: HTMLButtonElement;
  private shopping: StallShopping = null;
  private readonly askEl: HTMLElement;
  private readonly askText: HTMLElement;
  /** The display cell the open question would buy; nothing is sent until Yes. */
  private pendingBuy: number | null = null;
  /** Whether the open question is AskClosePrivateShop's rather than a buy. */
  private pendingPackUp = false;
  private stall: StallWindow | null = null;
  private gold = 0;
  private selfId = 0;
  private fingerprint = "";
  private readonly tooltip: ItemTooltip;

  constructor(
    container: HTMLElement,
    private readonly actions: StallActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "stall m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");

    this.titleEl = elem(doc, "div", "m2-titlebar stall-title");
    // The title bar's close event (`uishop.py:98`).
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close stall-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.dismiss());

    this.grid = elem(doc, "div", "shop-grid stall-list");

    this.emptyEl = elem(doc, "div", "shop-empty stall-empty");
    this.emptyEl.hidden = true;

    this.packUp = doc.createElement("button");
    this.packUp.type = "button";
    this.packUp.className = "stall-packup m2-large";
    // `PRIVATE_SHOP_CLOSE_BUTTON` (`locale_interface.txt:268`) -- the seller's
    // own button to end their stall, shown on your own shop (`uishop.py:165-170`).
    this.packUp.textContent = "Close shop";
    this.packUp.hidden = true;
    // AskClosePrivateShop: the question first, /close_shop only on accept
    // (`uishop.py:233-247`).
    this.packUp.addEventListener("click", () => {
      this.pendingBuy = null;
      this.pendingPackUp = true;
      this.askText.textContent = PRIVATE_SHOP_CLOSE_QUESTION;
      this.askEl.hidden = false;
    });

    // BuyButton and SellButton, toggles at (21, 295) and (104, 295) under
    // SHOP_BUY / SHOP_SELL (`shopdialog.py:66-100`, `locale_interface.txt:296-297`),
    // placed by the counter's own `.shop-buy` / `.shop-sell` rules.
    this.buyBtn = elem(doc, "button", "shop-buy m2-middle", "Buy") as HTMLButtonElement;
    this.sellBtn = elem(doc, "button", "shop-sell m2-middle", "Sell") as HTMLButtonElement;
    this.buyBtn.type = this.sellBtn.type = "button";
    this.buyBtn.hidden = this.sellBtn.hidden = true;
    this.buyBtn.addEventListener("click", () => this.toggleShopping("buy"));
    this.sellBtn.addEventListener("click", () => this.toggleShopping("sell"));

    // `AskBuyItem`'s QuestionDialog (`uishop.py:410-423`): Yes buys, No closes.
    this.askEl = elem(doc, "div", "stall-ask m2-board");
    this.askEl.hidden = true;
    this.askText = elem(doc, "div", "stall-ask-text");
    const yes = elem(doc, "button", "stall-ask-yes m2-middle", YES) as HTMLButtonElement;
    const no = elem(doc, "button", "stall-ask-no m2-middle", NO) as HTMLButtonElement;
    yes.type = no.type = "button";
    yes.addEventListener("click", () => {
      const slot = this.pendingBuy;
      const packUp = this.pendingPackUp;
      this.closeAsk();
      if (packUp) this.actions.onPackUp?.();
      else if (slot !== null) this.actions.onBuy?.(slot);
    });
    no.addEventListener("click", () => this.closeAsk());
    this.askEl.append(this.askText, yes, no);

    this.root.append(this.titleEl, close, this.grid, this.emptyEl, this.buyBtn, this.sellBtn, this.packUp, this.askEl);
    container.append(this.root);
  }

  /** A toggle pressed: down runs `OnBuy` / `OnSell`, which raise the other,
   *  and a down toggle pressed again runs `CancelShopping` (`uishop.py:88-92,258-271`). */
  private toggleShopping(mode: "buy" | "sell"): void {
    if (this.shopping === mode) {
      this.setShopping(null);
      return;
    }
    this.setShopping(mode);
    this.actions.onInfo?.(mode === "buy" ? SHOP_BUY_INFO : SHOP_SELL_INFO);
  }

  private setShopping(mode: StallShopping): void {
    if (this.shopping === mode) return;
    this.shopping = mode;
    this.buyBtn.classList.toggle("shop-mode-on", mode === "buy");
    this.sellBtn.classList.toggle("shop-mode-on", mode === "sell");
    this.actions.onShopping?.(mode);
  }

  /** Whose client this is, so the panel can tell its own stall from a
   *  stranger's. Set once, from `Welcome`. */
  setSelf(entityId: number): void {
    this.selfId = entityId;
  }

  update(stall: StallWindow): void {
    if (stall.seller_id === 0) {
      this.hide();
      return;
    }
    this.stall = stall;
    this.root.hidden = false;
    this.paint();
  }

  /** What the player can afford, from the last `Bag`. */
  setGold(gold: number): void {
    this.gold = gold;
    if (this.showing) this.paint();
  }

  private paint(): void {
    const stall = this.stall;
    if (stall === null) return;
    const print = [
      stall.seller_id,
      stall.sign,
      stall.seller_name,
      ...stall.items.map(
        (i) =>
          `${i.slot}:${i.vnum}:${i.name}:${i.count}:${i.price}:` +
          `${this.gold >= i.price ? 1 : 0}`,
      ),
    ].join("|");
    if (print === this.fingerprint) return;
    this.fingerprint = print;

    const doc = this.root.ownerDocument;
    const mine = stall.seller_id === this.selfId;
    this.titleEl.textContent = stallTitle(stall.sign, stall.seller_name);
    this.packUp.hidden = !mine;
    // `Open` hides both toggles on the player's own stall (`uishop.py:165-181`).
    this.buyBtn.hidden = this.sellBtn.hidden = mine;
    if (mine) this.setShopping(null);

    const bySlot = new Map<number, StallSlot>();
    for (const shelf of stall.items) bySlot.set(shelf.slot, shelf);
    const cells: HTMLElement[] = [];
    for (let cell = 0; cell < SHOP_SLOT_COUNT; cell++) {
      const shelf = bySlot.get(cell);
      const node = shelf === undefined ? elem(doc, "div", "shop-slot m2-slot") : this.shelfNode(doc, shelf);
      // ItemSlot's 5 x 8 cells, row by row (`shopdialog.py:52-59`).
      node.style.setProperty("grid-column", String((cell % SHOP_COLUMNS) + 1));
      node.style.setProperty("grid-row", String(Math.floor(cell / SHOP_COLUMNS) + 1));
      cells.push(node);
    }
    this.grid.replaceChildren(...cells);

    // Honest about which emptiness this is. A stall cannot be opened with
    // nothing on it (`char.cpp:573-574`), so an empty one is a sold-out one.
    this.emptyEl.hidden = stall.items.length > 0;
    this.emptyEl.textContent = stall.items.length > 0
      ? ""
      : mine ? "Everything on it has sold." : "Everything on this stall has sold.";
  }

  private shelfNode(doc: Document, shelf: StallSlot): HTMLButtonElement {
    const row = doc.createElement("button");
    row.type = "button";
    const within = this.gold >= shelf.price;
    row.className = "stall-row shop-slot m2-slot shop-row" +
      (within ? "" : " stall-row-unaffordable shop-row-unaffordable");
    const label = shelf.count > 1 ? `${shelf.name} x${shelf.count}` : shelf.name;
    row.title = label;
    row.setAttribute("aria-label", label);
    const span = Math.min(iconCellsTall(shelf.vnum), SHOP_ROWS - Math.floor(shelf.slot / SHOP_COLUMNS));
    row.style.setProperty("--shop-item-span", String(span));
    if (span > 1) row.style.zIndex = "1";
    row.append(itemArtNode(shelf.vnum, shelf.kind, doc, "shop-glyph"));
    if (shelf.count > 1) row.append(elem(doc, "span", "shop-count", String(shelf.count)));
    row.append(elem(doc, "span", "stall-row-price shop-price", money(shelf.price)));
    row.addEventListener("click", () => {
      this.pendingPackUp = false;
      this.pendingBuy = shelf.slot;
      this.askText.textContent = buyQuestion(shelf.name, shelf.count, shelf.price);
      this.askEl.hidden = false;
    });
    attachHoverTooltip(row, this.tooltip, () => shelfTooltipData(shelf));
    return row;
  }

  /** Close because the player asked. Tells the server, so its record of the
   *  open window goes with the panel. */
  dismiss(): void {
    if (!this.showing) return;
    this.hide();
    this.actions.onClose?.();
  }

  /** Close without telling anyone -- for a frame that already said so. */
  hide(): void {
    this.root.hidden = true;
    this.stall = null;
    this.fingerprint = "";
    this.grid.replaceChildren();
    this.emptyEl.hidden = true;
    this.packUp.hidden = true;
    this.buyBtn.hidden = this.sellBtn.hidden = true;
    this.setShopping(null); // Close runs CancelShopping (uishop.py:222)
    this.closeAsk();
    this.tooltip.hide();
  }

  private closeAsk(): void {
    this.pendingBuy = null;
    this.pendingPackUp = false;
    this.askEl.hidden = true;
  }

  /** Whose stall is on screen, or null when none is. */
  get seller(): number | null {
    return this.showing && this.stall !== null ? this.stall.seller_id : null;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }
}

/** One `itemStock` line: the bag cell listed and the price it was accepted at
 *  (`uiprivateshopbuilder.py:305-306`). */
interface StockLine {
  readonly bagSlot: number;
  readonly price: number;
}

export class StallBuilder {
  private readonly root: HTMLElement;
  private readonly signInput: HTMLInputElement;
  private readonly grid: HTMLElement;
  private readonly openButton: HTMLButtonElement;
  private readonly priceBox: HTMLElement;
  private readonly priceInput: HTMLInputElement;
  private readonly priceMoney: HTMLElement;
  private bag: BagModel = { gold: 0, capacity: 0, slots: [], worn: [] };
  /** `itemStock`, keyed by the grid cell each line was dropped on
   *  (`uiprivateshopbuilder.py:123`). */
  private readonly stock = new Map<number, StockLine>();
  /** `g_itemPriceDict`: the last price per vnum, primed by the server's
   *  `MyShopPriceList` lines and by every accepted price
   *  (`uiprivateshopbuilder.py:17`, `:41-50`, `char_item.cpp:1510-1538`). */
  private readonly remembered = new Map<number, number>();
  /** The drop the open price dialog is for (`uiprivateshopbuilder.py:250-254`). */
  private pending: { readonly bagSlot: number; readonly vnum: number; readonly cell: number } | null = null;
  private readonly tooltip: ItemTooltip;
  /** What the grid was last drawn from; null forces the next draw. */
  private fingerprint: string | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: StallBuilderActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "stall-builder m2-board");
    this.root.hidden = true;

    const title = elem(doc, "div", "m2-titlebar stall-title");
    // `PRIVATE_SHOP_TITLE` (`locale_interface.txt:270`, `privateshopbuilder.py:43`).
    title.textContent = "Open private shop";
    // The title bar's close event (`uiprivateshopbuilder.py:153`).
    const cross = doc.createElement("button");
    cross.type = "button";
    cross.className = "m2-close";
    cross.title = "Close";
    cross.setAttribute("aria-label", "Close");
    cross.addEventListener("click", () => this.close());

    this.signInput = doc.createElement("input");
    this.signInput.type = "text";
    this.signInput.className = "stall-sign-input";
    // `PRIVATE_SHOP_INPUT_NAME_DIALOG_TITLE` (`interfacemodule.py:1194`, `locale_game.txt:363`).
    this.signInput.placeholder = "Name of the private shop";
    // The server truncates rather than refusing (`char.cpp:592-593`).
    this.signInput.maxLength = SIGN_MAX_CHARS;
    this.signInput.addEventListener("input", () => this.refreshButton());

    this.grid = elem(doc, "div", "shop-grid stall-builder-grid");

    this.openButton = doc.createElement("button");
    this.openButton.type = "button";
    this.openButton.className = "stall-open m2-middle";
    // "OkButton", 61x21, `uiScriptLocale.OK` (`privateshopbuilder.py:94-103`, `locale_interface.txt:208`).
    this.openButton.textContent = "OK";
    this.openButton.addEventListener("click", () => this.submit());

    // CloseButton, `uiScriptLocale.CLOSE` (`privateshopbuilder.py:110-121`).
    const close = elem(doc, "button", "stall-builder-close m2-middle", "Close") as HTMLButtonElement;
    close.type = "button";
    close.addEventListener("click", () => this.close());

    this.root.append(title, cross, this.signInput, this.grid, this.openButton, close);
    // `OnPressEscapeKey` closes the builder and consumes Escape (`uiprivateshopbuilder.py:334-336`).
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });

    // MoneyInputDialog (`uicommon.py:333-418`, `moneyinputdialog.py`).
    this.priceBox = elem(doc, "div", "stall-price-dialog m2-board");
    this.priceBox.hidden = true;
    this.priceBox.setAttribute("role", "dialog");
    const priceTitle = elem(doc, "div", "m2-titlebar", PRIVATE_SHOP_INPUT_PRICE_DIALOG_TITLE);
    const priceCross = doc.createElement("button");
    priceCross.type = "button";
    priceCross.className = "m2-close";
    priceCross.title = "Close";
    priceCross.setAttribute("aria-label", "Close");
    this.priceInput = doc.createElement("input");
    this.priceInput.type = "text";
    this.priceInput.inputMode = "numeric";
    this.priceInput.className = "stall-price-input";
    this.priceInput.maxLength = PRICE_MAX_DIGITS;
    this.priceInput.setAttribute("aria-label", PRIVATE_SHOP_INPUT_PRICE_DIALOG_TITLE);
    this.priceInput.addEventListener("input", () => this.showMoney());
    this.priceMoney = elem(doc, "div", "stall-price-money");
    const ok = elem(doc, "button", "stall-price-ok m2-middle", "OK") as HTMLButtonElement; // locale_interface.txt:208
    const cancel = elem(doc, "button", "stall-price-cancel m2-middle", "Cancel") as HTMLButtonElement; // locale_interface.txt:4
    ok.type = cancel.type = "button";
    // SetAcceptEvent: the button and OnIMEReturn; SetCancelEvent: the board's
    // close, the button and OnPressEscapeKey (`uicommon.py:388-395`).
    ok.addEventListener("click", () => this.acceptPrice());
    cancel.addEventListener("click", () => this.closePrice());
    priceCross.addEventListener("click", () => this.closePrice());
    this.priceInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        this.acceptPrice();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.closePrice();
      }
    });
    this.priceBox.append(priceTitle, priceCross, this.priceInput, this.priceMoney, ok, cancel);

    container.append(this.root, this.priceBox);
  }

  /** The bag, as last sent. The grid reads each listed cell live, as `Refresh`
   *  does (`uiprivateshopbuilder.py:211-216`). */
  setBag(bag: BagModel): void {
    this.bag = bag;
    if (this.showing) this.paint();
  }

  /** `Open`: an empty stock and a fresh grid (`uiprivateshopbuilder.py:170-182`). */
  open(): void {
    this.stock.clear();
    this.closePrice();
    this.root.hidden = false;
    this.fingerprint = null;
    this.paint();
  }

  /** `Close`: the stock goes with the window, and nothing is sent
   *  (`uiprivateshopbuilder.py:187-194`). */
  close(): void {
    this.root.hidden = true;
    this.stock.clear();
    this.closePrice();
    this.tooltip.hide();
  }

  toggle(): boolean {
    if (this.showing) this.close();
    else this.open();
    return this.showing;
  }

  /** What OK would send: one line per listed cell whose bag cell still holds
   *  something, as the wire's three fields and no others. */
  get draft(): readonly StallDraftLine[] {
    return [...this.stock.entries()]
      .filter(([, line]) => listable(this.held(line.bagSlot)))
      .sort(([a], [b]) => a - b)
      .map(([cell, line]) => ({ sourceSlot: line.bagSlot, displayPos: cell, price: line.price }));
  }

  /** The sign as typed, trimmed. */
  get sign(): string {
    return this.signInput.value.trim();
  }

  /** Set the sign, with the button state a keystroke would leave. */
  setSign(text: string): void {
    this.signInput.value = text.slice(0, SIGN_MAX_CHARS);
    this.refreshButton();
  }

  /** One `MyShopPriceList <vnum> <price>` line (`game.py:1949-1950`). A price of
   *  zero is never offered back, as `GetPrivateShopItemPrice`'s caller only
   *  fills the dialog above zero (`uiprivateshopbuilder.py:247`). */
  rememberPrice(vnum: number, price: number): void {
    if (price <= 0) return;
    this.remembered.set(vnum, price);
  }

  /** `OnSelectEmptySlot` for a bag cell released over grid cell `cell`: the
   *  price dialog opens, filled with the remembered price for that vnum, and
   *  nothing is listed until it is accepted (`uiprivateshopbuilder.py:220-254`).
   *  A drop whose picked cells hold an item is `OnSelectItemSlot` with an item
   *  attached, which only plays the fail sound (`:258-261`). */
  offer(bagSlot: number, cell: number): void {
    if (!this.showing || !Number.isInteger(cell) || cell < 0 || cell >= SHOP_SLOT_COUNT) return;
    const held = this.held(bagSlot);
    if (held === undefined || !listable(held)) return;
    const rows = iconCellsTall(held.vnum);
    const top = pickedCell(cell, rows);
    for (let row = 0; row < rows; row++) {
      if (this.ownerOf(top + row * SHOP_COLUMNS) !== null) {
        this.actions.sfx?.play("loginfail"); // uiprivateshopbuilder.py:260
        return;
      }
    }
    // ANTIFLAG_GIVE or ANTIFLAG_MYSHOP: refused before the price dialog opens,
    // chat only, no sound (`uiprivateshopbuilder.py:235-237`).
    if (STALL_FORBIDDEN.has(held.vnum)) {
      this.actions.onInfo?.(PRIVATE_SHOP_CANNOT_SELL_ITEM);
      return;
    }
    this.pending = { bagSlot, vnum: held.vnum, cell: top };
    const remembered = this.remembered.get(held.vnum) ?? 0; // uiprivateshopbuilder.py:45-50
    this.priceInput.value = remembered > 0 ? String(remembered) : ""; // uiprivateshopbuilder.py:247-248
    this.showMoney();
    this.priceBox.hidden = false;
    this.priceInput.focus();
  }

  /** `CGridSlotWindow::OnRenderPickingSlot`: while an item is attached over the
   *  grid, the cells it would land on light up -- white while every one is
   *  free, red when `CheckMoving` finds one already listed
   *  (`PythonGridSlotWindow.cpp:20-46`, `:343-360`). `vnum` is the item
   *  attached (for its span); `cell` null clears the highlight, as a release
   *  or a drag that has left the grid does. */
  hover(vnum: number, cell: number | null): void {
    for (const node of this.grid.querySelectorAll(".shop-slot-hover-ok, .shop-slot-hover-bad")) {
      node.classList.remove("shop-slot-hover-ok", "shop-slot-hover-bad");
    }
    if (cell === null || !this.showing) return;
    if (!Number.isInteger(cell) || cell < 0 || cell >= SHOP_SLOT_COUNT) return;
    const rows = iconCellsTall(vnum);
    const top = pickedCell(cell, rows);
    const covered: number[] = [];
    let ok = true;
    for (let row = 0; row < rows; row++) {
      const index = top + row * SHOP_COLUMNS;
      if (index >= SHOP_SLOT_COUNT) continue;
      covered.push(index);
      if (this.ownerOf(index) !== null) ok = false; // CheckMoving: occupied by a different item
    }
    const cls = ok ? "shop-slot-hover-ok" : "shop-slot-hover-bad";
    for (const index of covered) {
      this.grid.querySelector(`[data-stall-builder-cell="${index}"]`)?.classList.add(cls);
    }
  }

  /** `AcceptInputPrice` (`uiprivateshopbuilder.py:275-314`). */
  private acceptPrice(): void {
    const pending = this.pending;
    if (pending === null) return; // uiprivateshopbuilder.py:277-278
    const price = parsePrice(this.priceInput.value);
    if (price === null) return; // uiprivateshopbuilder.py:282-289
    for (const [cell, line] of this.stock) {
      if (line.bagSlot === pending.bagSlot) this.stock.delete(cell); // uiprivateshopbuilder.py:295-298
    }
    this.remembered.set(pending.vnum, price); // uiprivateshopbuilder.py:302-303
    this.stock.set(pending.cell, { bagSlot: pending.bagSlot, price }); // uiprivateshopbuilder.py:305-306
    this.actions.sfx?.play("drop"); // uiprivateshopbuilder.py:307
    this.closePrice();
    this.paint();
  }

  /** `OnSelectItemSlot` with nothing attached: the line comes off the grid
   *  (`uiprivateshopbuilder.py:263-273`). */
  private takeBack(cell: number): void {
    if (!this.stock.delete(cell)) return;
    this.actions.sfx?.play("drop"); // uiprivateshopbuilder.py:269
    this.paint();
  }

  private closePrice(): void {
    this.pending = null;
    this.priceBox.hidden = true;
  }

  /** `__OnValueUpdate` (`uicommon.py:407-418`). */
  private showMoney(): void {
    const text = this.priceInput.value;
    const value = /^\d+$/.test(text) ? Number(text) : 0;
    this.priceMoney.textContent = MONEY_INPUT_DIALOG_SELLPRICE + formatYang(value);
  }

  private held(bagSlot: number): BagModel["slots"][number] | undefined {
    return this.bag.slots.find((slot) => slot.slot === bagSlot);
  }

  /** The listed cell whose item covers `cell`, or null when none does. */
  private ownerOf(cell: number): number | null {
    for (const [top, line] of this.stock) {
      const held = this.held(line.bagSlot);
      if (!listable(held)) continue;
      const rows = Math.min(iconCellsTall(held!.vnum), SHOP_ROWS - Math.floor(top / SHOP_COLUMNS));
      if (cell >= top && (cell - top) % SHOP_COLUMNS === 0 && (cell - top) / SHOP_COLUMNS < rows) return top;
    }
    return null;
  }

  private submit(): void {
    const listings = this.draft;
    // OnOk returns without a title or stock (`uiprivateshopbuilder.py:322-326`).
    if (listings.length === 0 || this.sign.length === 0) return;
    this.actions.onOpen?.(this.sign, listings);
    // The panel stays up until a `StallSign` says the stall exists.
  }

  private refreshButton(): void {
    this.openButton.disabled = this.draft.length === 0 || this.sign.length === 0;
  }

  /** `Refresh` (`uiprivateshopbuilder.py:199-218`). */
  private paint(): void {
    const print = [...this.stock.entries()]
      .map(([cell, line]) => {
        const s = this.held(line.bagSlot);
        return `${cell}:${line.bagSlot}:${line.price}:` +
          (s === undefined ? "-" : `${s.vnum}:${s.count}:${s.name}:${s.socket ?? 0}:${s.socket1 ?? 0}:${s.socket2 ?? 0}`);
      })
      .join("|");
    if (print === this.fingerprint) {
      this.refreshButton();
      return;
    }
    this.fingerprint = print;

    const doc = this.root.ownerDocument;
    const cells: HTMLElement[] = [];
    for (let cell = 0; cell < SHOP_SLOT_COUNT; cell++) {
      const node = elem(doc, "div", "shop-slot m2-slot");
      node.dataset.stallBuilderCell = String(cell);
      // ItemSlot's 5 x 8 cells, row by row (`privateshopbuilder.py:82-87`).
      node.style.setProperty("grid-column", String((cell % SHOP_COLUMNS) + 1));
      node.style.setProperty("grid-row", String(Math.floor(cell / SHOP_COLUMNS) + 1));
      node.addEventListener("click", () => {
        const owner = this.ownerOf(cell);
        if (owner !== null) this.takeBack(owner);
      });
      const line = this.stock.get(cell);
      const held = line === undefined ? undefined : this.held(line.bagSlot);
      if (line !== undefined && held !== undefined && listable(held)) {
        node.className = `shop-slot m2-slot shop-row shop-slot-${held.kind}`;
        const span = Math.min(iconCellsTall(held.vnum), SHOP_ROWS - Math.floor(cell / SHOP_COLUMNS));
        node.style.setProperty("--shop-item-span", String(span));
        if (span > 1) node.style.zIndex = "1";
        node.append(itemArtNode(held.vnum, held.kind, doc, "shop-glyph"));
        // A count of one is drawn as none (`uiprivateshopbuilder.py:213-216`).
        if (held.count > 1) node.append(elem(doc, "span", "shop-count", String(held.count)));
        const price = line.price;
        // SetPrivateShopBuilderItem: the price, then the cell's own item (`uitooltip.py:615-631`).
        attachHoverTooltip(node, this.tooltip, () => {
          const now = this.held(line.bagSlot);
          return now === undefined ? null : slotTooltipData(now, { price });
        });
      }
      cells.push(node);
    }
    this.grid.replaceChildren(...cells);
    this.refreshButton();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
